'use es6';

import { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import ErrorBoundary from 'customer-data-objects-ui-components/ErrorBoundary';
import SidebarCardError from 'customer-data-sidebar/SidebarCardError';
import { PermissionsContext } from './context/PermissionsContext';
import UIExpandableSection from 'UIComponents/accordion/UIExpandableSection';
import styled from 'styled-components';
import { CardStateManipulationContext } from './context/CardStateManipulationContext';
import { CrmMessagingContext } from 'crm-message-bus/CrmMessagingContext';
import { SourceAppContext } from './context/SourceAppContext';
import CrmLogger from 'customer-data-tracking/loggers/CrmLogger';
import { setupCsat } from 'wootric-nps/csat/csat-setup';
import { DragHandle as FoundationsDragHandle } from 'foundations-components/transitional/components/DragHandle';
import { jsx as _jsx } from "react/jsx-runtime";
const ExpandableSectionWrapper = styled.div.withConfig({
  displayName: "UniversalSidebarCard__ExpandableSectionWrapper",
  componentId: "sc-178ovsl-0"
})(["", " position:relative;overflow-wrap:anywhere;"], ({
  isDragging
}) => isDragging ? 'opacity: 0;' : '');
const UniversalSidebarCard = ({
  objectType,
  objectId,
  data,
  draggable,
  Title,
  Actions,
  Content,
  isDragging,
  DragHandle = FoundationsDragHandle,
  Header,
  cardProps = {}
}) => {
  const {
    onCardOpenChange
  } = useContext(CardStateManipulationContext);
  const {
    canEditSubject,
    readOnly
  } = useContext(PermissionsContext);
  const {
    sourceApp
  } = useContext(SourceAppContext);
  const isOpen = !data.isCollapsed;
  const legacyCardName = data.legacyCardName;
  const handleChangeCardOpen = useCallback(({
    target: {
      value
    }
  }) => {
    onCardOpenChange({
      legacyCardName,
      cardId: data.cardId,
      isOpen: value
    });
  }, [onCardOpenChange, legacyCardName, data.cardId]);
  const handleTrackCardInteraction = useCallback(() => {
    if (typeof data.cardId === 'string' && data.cardId.includes('EXTENSION')) {
      setupCsat('CSAT_CRM_CUSTOM_CARDS');
    }
    CrmLogger.log('sidebar-interaction', {
      type: objectType,
      action: 'interacted with a card',
      cardId: data.cardId,
      sourceApp
    });
  }, [data.cardId, objectType, sourceApp]);

  // todo - look into passing the component name to ErrorBoundary
  // for more detailed reporting
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    ErrorComponent: SidebarCardError,
    boundaryName: "UniversalSidebarCard_SidebarError",
    showRefreshAlert: false,
    children: /*#__PURE__*/_jsx(CrmMessagingContext.Provider, {
      value: {
        messagingId: data.cardId
      },
      children: /*#__PURE__*/_jsx(ExpandableSectionWrapper, {
        isDragging: isDragging,
        children: /*#__PURE__*/_jsx(UIExpandableSection, {
          actions: Actions && /*#__PURE__*/_jsx(Actions, Object.assign({
            data: data,
            objectType: objectType,
            subjectId: objectId,
            objectId: objectId,
            isEditable: canEditSubject && !readOnly
          }, cardProps)),
          titleText: /*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "UniversalSidebarCard_SidebarError_titleText",
            showRefreshAlert: false,
            children: /*#__PURE__*/_jsx("span", {
              "data-selenium-test": "universal-sidebar-card-title",
              children: /*#__PURE__*/_jsx(Title, Object.assign({
                subjectId: objectId /* todo subjectId -> objectId */,
                objectId: objectId,
                objectType: objectType,
                data: data
              }, cardProps))
            })
          }),
          indentation: "compact",
          draggable: draggable,
          "data-test-id": "universal-sidebar-card-expandable-section",
          "data-sidebar-card-type": data.__typename,
          "data-sidebar-card-association-object-type-id": data.configuration && data.configuration.associationDefinitions[0].toObjectTypeId,
          "data-sidebar-key": legacyCardName,
          "data-selenium-expanded": isOpen,
          onOpenChange: handleChangeCardOpen,
          DragHandle: DragHandle,
          Header: Header
          /* use defaultOpen instead of open so that we don't have to wait on the gql response for card to close */,
          defaultOpen: !isDragging && isOpen,
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            ErrorComponent: SidebarCardError,
            boundaryName: "UniversalSidebarCard_SidebarError_Content",
            showRefreshAlert: false,
            children: /*#__PURE__*/_jsx("div", {
              "data-selenium-test": "universal-sidebar-card-content",
              "data-test-id": "universal-sidebar-card-content",
              onClick: handleTrackCardInteraction,
              children: /*#__PURE__*/_jsx(Content, Object.assign({
                subjectId: objectId /* todo subjectId -> objectId */,
                objectId: objectId,
                objectType: objectType,
                data: data
                /* added for legacy cards (i.e. ProfileAttachments), for newer cards it's preferred to grab from PermissionsContext as canEditSubject */,
                isEditable: canEditSubject && !readOnly
              }, cardProps))
            })
          })
        })
      })
    })
  });
};
UniversalSidebarCard.propTypes = {
  Content: PropTypes.func.isRequired,
  Title: PropTypes.func.isRequired,
  Actions: PropTypes.func,
  cardProps: PropTypes.object,
  data: PropTypes.shape({
    __typename: PropTypes.string.isRequired,
    label: PropTypes.string,
    isCollapsed: PropTypes.bool,
    legacyCardName: PropTypes.string,
    cardId: PropTypes.string.isRequired,
    configuration: PropTypes.shape({
      associationDefinitions: PropTypes.array
    })
  }).isRequired,
  objectType: PropTypes.string.isRequired,
  objectId: PropTypes.string.isRequired,
  isDragging: PropTypes.bool,
  DragHandle: PropTypes.elementType,
  Header: PropTypes.elementType,
  draggable: PropTypes.bool
};
export default UniversalSidebarCard;